import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Copy } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllEmails } from '../../utils/emailHelpers';

/**
 * Composant pour éditer une liste d'emails avec validation
 */
const EmailListEditor = ({
  emails,
  onChange,
  label,
  placeholder,
  isEditing = false,
  validateEmail
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  // Normaliser les emails en array
  const emailList = getAllEmails(emails);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingIndex]);

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success('Copié dans le presse-papier !');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };

  const handleAddEmail = () => {
    if (!newEmail.trim()) return;

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setValidationError('Format d\'email invalide');
      return;
    }

    // Validation personnalisée (pro/perso)
    if (validateEmail && !validateEmail(newEmail)) {
      return; // L'erreur est gérée par la fonction de validation externe
    }

    // Vérifier les doublons
    if (emailList.includes(newEmail.trim())) {
      setValidationError('Cet email existe déjà');
      return;
    }

    // Ajouter l'email
    const updatedEmails = [...emailList, newEmail.trim()];
    onChange(updatedEmails);
    setNewEmail('');
    setValidationError(null);
  };

  const handleRemoveEmail = (indexToRemove) => {
    const updatedEmails = emailList.filter((_, index) => index !== indexToRemove);
    onChange(updatedEmails);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const startEditing = (index, email) => {
    setEditingIndex(index);
    setEditValue(email);
  };

  const handleSaveEdit = () => {
    if (!editValue.trim()) {
      setEditingIndex(null);
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editValue)) {
      toast.error('Format d\'email invalide');
      return;
    }

    // Validation personnalisée (pro/perso)
    if (validateEmail && !validateEmail(editValue)) {
      return;
    }

    // Vérifier les doublons (sauf pour l'email actuel)
    const otherEmails = emailList.filter((_, index) => index !== editingIndex);
    if (otherEmails.includes(editValue.trim())) {
      toast.error('Cet email existe déjà');
      return;
    }

    // Mettre à jour l'email
    const updatedEmails = [...emailList];
    updatedEmails[editingIndex] = editValue.trim();
    onChange(updatedEmails);
    setEditingIndex(null);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  return (
    <div className="space-y-2">
      {/* Liste des emails existants */}
      {emailList.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {emailList.map((email, index) => (
            <div key={index} className="flex items-center gap-2 group bg-gray-200 rounded-xl px-2 py-1">
              {editingIndex === index ? (
                // Mode édition
                <div className="flex items-center gap-1">
                  <input
                    ref={inputRef}
                    type="email"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleSaveEdit}
                    onKeyDown={handleEditKeyDown}
                    className="border border-indigo-500 p-1 rounded text-sm focus:ring-2 focus:ring-indigo-500 w-64"
                  />
                </div>
              ) : (
                // Mode affichage
                <>
                  <span
                    onClick={() => isEditing && startEditing(index, email)}
                    className={`text-sm text-gray-700 ${isEditing ? 'cursor-pointer hover:text-indigo-600 transition-colors' : ''}`}
                    title={isEditing ? "Cliquer pour modifier" : email}
                  >
                    {email}
                  </span>
                  <button
                    onClick={() => copyToClipboard(email, index)}
                    className={`p-1 hover:bg-gray-200 rounded transition-colors ${isEditing ? '' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Copier l'email"
                  >
                    <Copy size={14} className={copiedIndex === index ? 'text-green-500' : 'text-gray-500'} />
                  </button>
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveEmail(index)}
                      className="p-1  rounded transition-colors"
                      title="Supprimer cet email"
                    >
                      <X size={14} className="text-red-500" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Formulaire d'ajout en mode édition */}
      {isEditing && (
        <div className="inline-flex flex-col gap-1 pt-1">
          <div className="flex gap-1.5">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                setValidationError(null);
              }}
              onKeyDown={handleKeyDown}
              className={`w-64 border p-1.5 rounded text-sm focus:ring-2 ${
                validationError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-indigo-500'
              }`}
              placeholder={placeholder || "Ajouter un email..."}
            />
            <button
              onClick={handleAddEmail}
              className="px-2 py-1.5 border  rounded-full hover:bg-gray-50 transition-colors text-sm flex items-center gap-1"
              title="Ajouter"
            >
              <Plus size={14} className='text-gray-400' />
            </button>
          </div>
          {validationError && (
            <p className="mt-1 text-xs text-red-600">{validationError}</p>
          )}
        </div>
      )}

      {/* Message si aucun email et pas en édition */}
      {!isEditing && emailList.length === 0 && (
        <p className="text-sm text-gray-400 italic">Aucun email</p>
      )}
    </div>
  );
};

export default EmailListEditor;
